/* One Event — STORY-011.
 *
 * ── every field saves on its own ─────────────────────────────────────────────
 * That is the acceptance criterion this screen exists to satisfy, and it is why
 * each section is its own form with its own button rather than one big Save.
 * Each sends only what it changed: an absent key leaves a field alone and
 * `null` clears it, so saving the name cannot wipe a budget.
 *
 * ── the budget lives here, not on the create form ────────────────────────────
 * Decided 2026-08-13 from the prototype: you plan a night's budget while
 * looking at the night. Behind an "Edit" button on the create form it was
 * hidden well enough to be reported as missing by somebody looking straight at
 * it. Creating an Event therefore never asks for money, which suits an idea.
 */

import type { Event, Money } from '@eventuley/contracts';
import {
  amountOf,
  type CurrencyCode,
  type MoneyParseError,
  minorUnitDigits,
  parseCurrency,
  parseMoney,
} from '@eventuley/domain';
import { type MessageKey, zoneCity } from '@eventuley/i18n';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout, TextArea, TextField } from '../../../shared/ui/index.js';
import { GuestList } from '../../attendances/index.js';
import { useCurrentUser } from '../../auth/index.js';
import { Loadout } from '../../inventory/index.js';
import { EventMoney } from '../../money/index.js';
import { useEnabledValues } from '../../organisations/index.js';
import { VenuePicker } from '../../venues/index.js';
import { useEvent, useUpdateEvent } from '../api/index.js';
import { EventStatusBadge } from '../components/event-status-badge.js';
import { StatusControl } from '../components/status-control.js';
import { StatusHistory } from '../components/status-history.js';

export function EventScreen() {
  const t = useTranslate();
  const { organisationId = '', eventId = '' } = useParams({ strict: false });
  /* By id, not by filtering the list — added 2026-09-05.
   *
   * This screen used to call `useEvents` and pick its Event out of the page
   * that came back, which meant it could only ever show an Event the LIST
   * would show: not a completed or cancelled one, and not one past the first
   * twenty-five. Cancelling from this very page therefore landed on "that
   * event is not here", which reads as destruction — and Events cannot be
   * deleted at all. */
  const { data, error } = useEvent(organisationId, eventId);

  const event = data?.event;

  /* A missing Event is not a failure, and the two must not look alike.
   *
   * Fetching by id means "no such Event" now arrives as a 404 rather than as
   * an absence from a list, so it is separated here: `not_found` gets the
   * explanation and the way back below, and everything else gets the error
   * callout. Collapsing them would tell somebody whose request merely failed
   * that their Event does not exist — which, since Events cannot be deleted,
   * would be alarming and false. */
  const missing = error instanceof ApiError && error.code === 'not_found';

  if (error && !missing) {
    return (
      <Callout tone="danger">
        {error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
      </Callout>
    );
  }

  /* Missing is decided before loading, so the 404 shows its own message
   * rather than a spinner that never resolves. */
  if (!data && !missing) return <p className="field__hint">{t('common.loading')}</p>;

  if (!event) {
    /* Deleted, or never in this workspace. The list is the way back, and saying
     * so beats an empty screen that looks like a fault. */
    return (
      <Callout tone="warning">
        <strong>{t('events.detail.missing')}</strong>
        <p>
          <Link to="/orgs/$organisationId/events" params={{ organisationId }}>
            {t('events.detail.back')}
          </Link>
        </p>
      </Callout>
    );
  }

  return <EventDetail event={event} organisationId={organisationId} />;
}

function EventDetail({ event, organisationId }: { event: Event; organisationId: string }) {
  const t = useTranslate();
  /* ONE mutation for the whole screen, passed down.
   *
   * Each section used to call `useUpdateEvent` for itself, and this component
   * called it again for the callout and the live region — so the state those
   * two rendered belonged to a mutation nothing ever invoked. Its `error` was
   * permanently null and its `isSuccess` permanently false, which meant a
   * refused save produced no callout, no field error, and a button that simply
   * stopped spinning. Somebody clearing the name and pressing Save would have
   * been told nothing and believed it saved. Found by review, 2026-08-21. */
  const update = useUpdateEvent(organisationId, event.id);
  const { data: enabled } = useEnabledValues(organisationId);

  /* Today at the EVENT's zone, not the browser's — an Event on the 14th in
   * Istanbul is not over because it is still the 13th where the reader is.
   * `en-CA` because it formats as `YYYY-MM-DD`, which is what `event.date`
   * already is, so the comparison is a string comparison and needs no parsing.
   * The locale here is a formatting device, never the reader's. */
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: event.timezone }).format(new Date());

  const currencies = (enabled?.values ?? [])
    .filter((row) => row.kind === 'currency')
    .map((row) => row.value);

  return (
    <>
      <div className="page-head">
        <h1>{event.name}</h1>
        <EventStatusBadge status={event.status} />
      </div>

      {update.error ? (
        <Callout tone="danger">
          {update.error instanceof NetworkError
            ? t('error.network')
            : update.error instanceof ApiError && update.error.code === 'validation_failed'
              ? t('events.detail.rejected')
              : update.error instanceof ApiError && update.error.code === 'not_found'
                ? t('events.detail.missing')
                : t('error.unexpected')}
        </Callout>
      ) : null}

      {/* A live region, so a screen-reader user hears that a save landed. Every
          section shares it: only one save is in flight at a time in practice,
          and three regions would be three things announcing at once. */}
      <p className="sr-only" role="status">
        {update.isSuccess ? t('events.detail.saved') : ''}
      </p>

      {/* Immediately under the head, because "where does this stand" is the
          question somebody opens an Event to answer — and because the prompt it
          carries is about the fields below it. */}
      <StatusControl event={event} organisationId={organisationId} today={today} />

      <NameSection event={event} update={update} />
      <WhenSection event={event} update={update} organisationId={organisationId} />
      <BudgetSection event={event} update={update} currencies={currencies} />
      <NotesSection event={event} update={update} />

      {/* What it made, under the plan and above the guest list: the budget
          fields immediately above are what these figures are measured against,
          and reading them together is the whole point of showing both. */}
      <EventMoney organisationId={organisationId} eventId={event.id} />

      {/* The second entry point — STORY-029. Packing for a night is done while
          looking at the night, and it is the SAME action against the same
          records as the one on the Inventory screen. */}
      <Loadout organisationId={organisationId} eventId={event.id} />

      {/* Under the Event's own fields and above its history: the guest list is
          the work, and what happened to the Event is the record of it.

          Keyed on the Event, so which page somebody was reading does not
          follow them to a different night. Without it, moving from an Event
          where they were on page three to one with five guests asked the
          server for rows 21–30 of a list five long. */}
      <GuestList key={event.id} organisationId={organisationId} eventId={event.id} />

      {/* No timezone passed: a move records when a PERSON acted, so it reads in
          the reader's own zone — unlike every other time on this screen, which
          belongs to the Venue. */}
      <StatusHistory organisationId={organisationId} eventId={event.id} />

      <p className="auth__alt">
        <Link to="/orgs/$organisationId/events" params={{ organisationId }}>
          {t('events.detail.back')}
        </Link>
      </p>
    </>
  );
}

function NameSection({ event, update }: { event: Event; update: EventUpdate }) {
  const t = useTranslate();
  const [name, setName] = useState(event.name);

  /* Seeded from the server rather than held as a second copy: the field is the
   * draft, the query is the truth. */
  useEffect(() => setName(event.name), [event.name]);

  return (
    <section className="settings__section">
      <h2>{t('events.detail.name_heading')}</h2>
      <form
        onSubmit={(submitted) => {
          submitted.preventDefault();
          update.mutate({ name });
        }}
        noValidate
      >
        <TextField
          label={t('events.capture.name_label')}
          value={name}
          onChange={(changed) => setName(changed.target.value)}
        />
        <Button type="submit" busy={update.isPending}>
          {t('events.detail.save')}
        </Button>
      </form>
    </section>
  );
}

function WhenSection({
  event,
  update,
  organisationId,
}: {
  event: Event;
  update: EventUpdate;
  organisationId: string;
}) {
  const t = useTranslate();
  const { data: user } = useCurrentUser();
  const { data: enabled } = useEnabledValues(organisationId);

  const enabledZones = (enabled?.values ?? [])
    .filter((row) => row.kind === 'timezone')
    .map((row) => row.value);
  const isOwner =
    user?.memberships.find((held) => held.organisationId === organisationId)?.role === 'owner';
  const [date, setDate] = useState(event.date ?? '');
  /* `HH:MM` for the input, from the `HH:MM:SS` the column answers with. */
  const [time, setTime] = useState(event.time?.slice(0, 5) ?? '');

  useEffect(() => {
    setDate(event.date ?? '');
    setTime(event.time?.slice(0, 5) ?? '');
  }, [event.date, event.time]);

  const timeWithoutDate = time !== '' && date === '';

  return (
    <section className="settings__section">
      <h2>{t('events.detail.when_heading')}</h2>
      {/* The zone it is actually read in — resolved by the server from the
          Venue, or the workspace's when there is none. Shown rather than
          assumed, because "19:30" means nothing without it.

          Attaching a Venue in another country leaves this wall-clock time
          exactly as it is and changes only the line below — the zone is
          resolved on read and never stored on the Event. */}
      <p className="field__hint">
        {t('events.detail.reads_in', { zone: zoneCity(event.timezone) })}
      </p>

      {/* The Venue itself, with its inline create. Sending somebody to a
          settings screen mid-task loses the Event they were writing —
          STORY-013. */}
      <VenuePicker
        organisationId={organisationId}
        selected={event.venue}
        enabledTimezones={enabledZones}
        canManageTimezones={Boolean(isOwner)}
        busy={update.isPending}
        onChange={(venueId) => update.mutate({ venueId })}
      />

      <form
        onSubmit={(submitted) => {
          submitted.preventDefault();
          if (timeWithoutDate) return;
          /* `null` clears, which is what an emptied field means here. */
          update.mutate({ date: date === '' ? null : date, time: time === '' ? null : time });
        }}
        noValidate
      >
        <TextField
          label={t('events.capture.date_label')}
          type="date"
          value={date}
          onChange={(changed) => setDate(changed.target.value)}
        />
        <TextField
          label={t('events.capture.time_label')}
          type="time"
          value={time}
          onChange={(changed) => setTime(changed.target.value)}
          error={timeWithoutDate ? t('events.capture.time_needs_date') : undefined}
        />
        <Button type="submit" busy={update.isPending} disabled={timeWithoutDate}>
          {t('events.detail.save')}
        </Button>
      </form>
    </section>
  );
}

/** The one mutation this screen runs, shared by every section. */
type EventUpdate = ReturnType<typeof useUpdateEvent>;

/**
 * The two money figures, each with its own currency.
 *
 * Separate on purpose: making one follow the other would silently rewrite a
 * stored value. Both are optional and independent — an Event may have one,
 * both, or neither, and an idea usually has neither.
 *
 * ── parsed through the domain, never through `Number()` ─────────────────────
 * `1.234,50` is one thousand in Turkish and malformed in English, and the same
 * keystrokes are two different numbers in the two languages this product ships.
 * `lang-typescript.md` bans `Number()` on a money string for exactly that, and
 * this section did it anyway until review on 2026-08-21 — where a grouped
 * number parsed to NaN, became `null`, and CLEARED a budget somebody had
 * already saved. Silently, because the callout above was wired to a mutation
 * that never ran.
 */
function BudgetSection({
  event,
  update,
  currencies,
}: {
  event: Event;
  update: EventUpdate;
  currencies: readonly string[];
}) {
  const t = useTranslate();
  const { formatLocale } = useI18n();

  const [budget, setBudget] = useState(() => majorOf(event.plannedBudget, formatLocale));
  const [budgetCurrency, setBudgetCurrency] = useState(event.plannedBudget?.currency ?? '');
  const [revenue, setRevenue] = useState(() => majorOf(event.expectedRevenue, formatLocale));
  const [revenueCurrency, setRevenueCurrency] = useState(event.expectedRevenue?.currency ?? '');
  const [refusals, setRefusals] = useState<{
    budget?: MoneyParseError;
    revenue?: MoneyParseError;
  }>({});

  /* Defaulted to the workspace's first enabled currency ONCE it has arrived.
   *
   * Initialising from `currencies[0]` in `useState` captured it on the first
   * render, when the enabled set is still loading and the list is empty — so
   * the select stayed blank and a typed amount was sent as null, silently. */
  useEffect(() => {
    const fallback = currencies[0];
    if (!fallback) return;
    setBudgetCurrency((current) => current || (event.plannedBudget?.currency ?? fallback));
    setRevenueCurrency((current) => current || (event.expectedRevenue?.currency ?? fallback));
  }, [currencies[0], event.plannedBudget, event.expectedRevenue]);

  return (
    <section className="settings__section">
      <h2>{t('events.detail.budget_heading')}</h2>
      <p className="field__hint">{t('events.detail.budget_hint')}</p>

      <form
        onSubmit={(submitted) => {
          submitted.preventDefault();

          const parsedBudget = toMoney(budget, budgetCurrency, formatLocale);
          const parsedRevenue = toMoney(revenue, revenueCurrency, formatLocale);

          /* Refused rather than sent as null. Clearing a field is deliberate —
           * an empty box — and a number nobody could parse is a mistake. Sending
           * both as `null` made them the same thing, which is how a saved
           * budget disappeared. */
          if (!parsedBudget.ok || !parsedRevenue.ok) {
            setRefusals({
              ...(parsedBudget.ok ? {} : { budget: parsedBudget.error }),
              ...(parsedRevenue.ok ? {} : { revenue: parsedRevenue.error }),
            });
            return;
          }

          setRefusals({});
          update.mutate({
            plannedBudget: parsedBudget.value,
            expectedRevenue: parsedRevenue.value,
          });
        }}
        noValidate
      >
        <TextField
          label={t('events.detail.budget_label')}
          inputMode="decimal"
          value={budget}
          onChange={(changed) => setBudget(changed.target.value)}
          error={refusals.budget ? t(MONEY_REFUSALS[refusals.budget]) : undefined}
        />
        <CurrencyField
          label={t('events.detail.budget_currency_label')}
          value={budgetCurrency}
          currencies={currencies}
          onChange={setBudgetCurrency}
        />

        <TextField
          label={t('events.detail.revenue_label')}
          inputMode="decimal"
          value={revenue}
          onChange={(changed) => setRevenue(changed.target.value)}
          error={refusals.revenue ? t(MONEY_REFUSALS[refusals.revenue]) : undefined}
        />
        <CurrencyField
          label={t('events.detail.revenue_currency_label')}
          value={revenueCurrency}
          currencies={currencies}
          onChange={setRevenueCurrency}
        />

        <Button type="submit" busy={update.isPending}>
          {t('events.detail.save')}
        </Button>
      </form>
    </section>
  );
}

/* Named explicitly rather than built as `t(\`money.\${error}\`)` — `i18n.md`
 * forbids a dynamically constructed key. `empty` never reaches here: an empty
 * field is a deliberate clear, handled before the parse. */
const MONEY_REFUSALS = {
  empty: 'events.detail.money_invalid',
  'not-a-number': 'events.detail.money_invalid',
  'too-many-decimals': 'events.detail.money_decimals',
  'too-large': 'events.detail.money_too_large',
} as const satisfies Record<MoneyParseError, MessageKey>;

/**
 * The everyday picker: a native `<select>` over the ENABLED set — STORY-041.
 *
 * `design.md` prefers a real select where the behaviour permits one, and with a
 * handful of options it does. A value the workspace later disabled stays
 * listed and stays selected here, because enablement governs the picker and
 * never the stored record.
 */
function CurrencyField({
  label,
  value,
  currencies,
  onChange,
}: {
  label: string;
  value: string;
  currencies: readonly string[];
  onChange: (next: string) => void;
}) {
  const t = useTranslate();
  const options = currencies.includes(value) || value === '' ? currencies : [value, ...currencies];

  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <select
        className="field__control"
        value={value}
        onChange={(changed) => onChange(changed.target.value)}
      >
        {options.map((code) => (
          <option key={code} value={code}>
            {currencies.includes(code) ? code : t('events.detail.currency_disabled', { code })}
          </option>
        ))}
      </select>
    </label>
  );
}

function NotesSection({ event, update }: { event: Event; update: EventUpdate }) {
  const t = useTranslate();
  const [notes, setNotes] = useState(event.notes ?? '');

  useEffect(() => setNotes(event.notes ?? ''), [event.notes]);

  return (
    <section className="settings__section">
      <h2>{t('events.detail.notes_heading')}</h2>
      <form
        onSubmit={(submitted) => {
          submitted.preventDefault();
          update.mutate({ notes: notes === '' ? null : notes });
        }}
        noValidate
      >
        {/* A textarea, as `design/components/text-field` has specified for
            notes all along — this shipped as a single-line input, where Enter
            submits the form instead of starting a line. */}
        <TextArea
          label={t('events.detail.notes_label')}
          value={notes}
          onChange={(changed) => setNotes(changed.target.value)}
        />
        <Button type="submit" busy={update.isPending}>
          {t('events.detail.save')}
        </Button>
      </form>
    </section>
  );
}

/**
 * Minor units to a major-unit string for the field, using the CURRENCY's digit
 * count rather than two.
 *
 * JPY has none and KWD has three — `i18n.md` is explicit that any path
 * assuming two is a bug waiting for its first customer in the wrong country.
 *
 * Written with the reader's decimal separator, so what comes out of the field
 * is what `parseMoney` will accept back into it.
 */
function majorOf(money: Money | null, locale: string): string {
  if (!money) return '';

  const digits = minorUnitDigits(money.currency as CurrencyCode);
  if (digits === 0) return String(money.amount);

  const whole = Math.trunc(money.amount / 10 ** digits);
  const fraction = String(Math.abs(money.amount) % 10 ** digits).padStart(digits, '0');
  const separator = (1.1).toLocaleString(locale).replace(/\d/g, '');

  return `${whole}${separator}${fraction}`;
}

/**
 * And back, through the domain's parser.
 *
 * An empty field is a deliberate clear and answers `null`. Anything else that
 * cannot be parsed is a REFUSAL rather than a clear — the distinction this
 * function did not make until review, which is how a grouped number typed into
 * a filled field wiped the figure already there.
 */
function toMoney(
  major: string,
  currency: string,
  locale: string,
): { ok: true; value: Money | null } | { ok: false; error: MoneyParseError } {
  const trimmed = major.trim();
  if (trimmed === '') return { ok: true, value: null };

  /* No currency means the enabled set has not arrived, or failed to. Refused
   * rather than treated as a clear: the amount is real and the currency is
   * simply not known yet. */
  const code = parseCurrency(currency);
  if (!code) return { ok: false, error: 'not-a-number' };

  const parsed = parseMoney(trimmed, code, locale);
  if (!parsed.ok) return { ok: false, error: parsed.error };

  const amount = amountOf(parsed.value);
  if (amount < 0) return { ok: false, error: 'not-a-number' };

  return { ok: true, value: { amount, currency: code } };
}
